import { fetchPromo } from '@/api/v4/plans'
import { Plan } from '@/types'
import Cookie from 'js-cookie'
import Bugsnag from '@bugsnag/js'
import { defineStore } from 'pinia'
import { env } from '@/lib/runtimeEnv'

interface PlanState {
  plans: Plan[]
  promo: any
  selectedPlan: Plan | null
  clientSeats: number
  checkoutRedirect: string | null
  checkoutRedirectPath: string | null
  isLoadingPlans: boolean
}

interface PromoDto {
  promo: string | null
  retries: number
}

function getStoredPromoName(promo: any): string {
  return (
    promo?.promoId ||
    sessionStorage.getItem('js_promo') ||
    Cookie.get('promo') ||
    'default'
  )
}

function clearStoredPromoName(): void {
  sessionStorage.removeItem('js_promo')
  Cookie.remove('promo', {
    path: '/',
    domain: env.VITE_COOKIE_DOMAIN as string,
  })
}

function triggerGotPromo(promo): void {
  const event = new CustomEvent('gotPromo', {
    detail: {
      promoId: promo.promoId && promo.promoId !== '' ? promo.promoId : null,
    },
  })

  window.dispatchEvent(event)
}

export const usePlansStore = defineStore('plans', {
  state: (): PlanState => ({
    plans: [],
    promo: null,
    selectedPlan: null,
    clientSeats: 2,
    checkoutRedirect: null,
    isLoadingPlans: false
  }),
  getters: {
    promotedPlan(state) {
      return state.plans.find(p => p.isPromoted)
    }
  },
  actions: {
    async fetchPromo(
      promoDto: PromoDto = {
        promo: null,
        retries: 3,
      }
    ) {
      this.isLoadingPlans = true
      const promoName = promoDto.promo || getStoredPromoName(this.promo)

      const { data, error } = await fetchPromo(promoName)

      if (env.VITE_BUGSNAG_KEY) {
        Bugsnag.leaveBreadcrumb(
          'Fetched promo.',
          { promoName, promo: data },
          'state'
        )
      }
      if (error && error.status > 400) {
        clearStoredPromoName()

        if (promoDto.retries > 0) {
          this.fetchPromo({
            promo: 'default',
            retries: --promoDto.retries,
          })
        }

        if (env.VITE_BUGSNAG_KEY) {
          Bugsnag.notify(error)
        }
      }
      if (data) {
        sessionStorage.setItem('js_promo', data.promo.promoId)
        this.plans = data.plans
        this.promo = data.promo
        triggerGotPromo(data.promo)
      }
      this.isLoadingPlans = false
      return
    },
    setSelectedPlan(plan: Plan) {
      this.selectedPlan = plan
    },
    setClientSeats(seatCount: number) {
      this.clientSeats = seatCount
    },
    setCheckoutRedirect(checkoutRedirect: string) {
      this.checkoutRedirect = checkoutRedirect
    },
    setCheckoutRedirectPath(path: string) {
      this.checkoutRedirectPath = path
    }
  }
})
